import { useState, useEffect, useRef, useCallback } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, User, Upload, Camera } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const roleOptions = ["Dad", "Mom", "Teen", "Kid", "Grandpa", "Grandma", "Other"];

const colorOptions = [
  { value: "#6366f1", label: "Indigo" },
  { value: "#10b981", label: "Emerald" },
  { value: "#f59e0b", label: "Amber" },
  { value: "#ef4444", label: "Red" },
  { value: "#8b5cf6", label: "Violet" },
  { value: "#06b6d4", label: "Cyan" },
  { value: "#ec4899", label: "Pink" },
  { value: "#84cc16", label: "Lime" },
];

const defaultAvatars = [
  "https://images.unsplash.com/photo-1658909835269-e76abd3ffb5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwcGVvcGxlJTIwcG9ydHJhaXRzJTIwc21pbGluZyUyMHN0dWRpb3xlbnwwfHx8fDE3NzM2MDUwMzB8MA&ixlib=rb-4.1.0&q=85&w=200",
  "https://images.unsplash.com/photo-1737599819881-df2553a821ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwcGVvcGxlJTIwcG9ydHJhaXRzJTIwc21pbGluZyUyMHN0dWRpb3xlbnwwfHx8fDE3NzM2MDUwMzB8MA&ixlib=rb-4.1.0&q=85&w=200",
  "https://images.unsplash.com/photo-1570697754235-fb87d1021ec4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwcGVvcGxlJTIwcG9ydHJhaXRzJTIwc21pbGluZyUyMHN0dWRpb3xlbnwwfHx8fDE3NzM2MDUwMzB8MA&ixlib=rb-4.1.0&q=85&w=200",
];

export default function MembersPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    role: "Other",
    color: "#6366f1",
    avatar_url: "",
  });

  const fetchMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/members`);
      setMembers(res.data);
    } catch (error) {
      toast.error("Failed to fetch members");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleFileUpload = async (event, memberId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, GIF or WebP image");
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be less than 2MB");
      return;
    }

    setUploadingFor(memberId);
    
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`${API}/members/${memberId}/avatar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? res.data : m))
      );
      toast.success("Profile picture updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingFor(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const triggerFileUpload = (memberId) => {
    if (fileInputRef.current) {
      fileInputRef.current.dataset.memberId = memberId;
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      if (editingMember) {
        const res = await axios.put(
          `${API}/members/${editingMember.id}`,
          formData
        );
        setMembers((prev) =>
          prev.map((m) => (m.id === editingMember.id ? res.data : m))
        );
        toast.success("Member updated");
      } else {
        const res = await axios.post(`${API}/members`, formData);
        setMembers((prev) => [res.data, ...prev]);
        toast.success("Member added");
      }

      closeModal();
    } catch (error) {
      toast.error("Failed to save member");
    }
  };

  const deleteMember = async (id) => {
    try {
      await axios.delete(`${API}/members/${id}`);
      setMembers((prev) => prev.filter((m) => m.id !== id));
      toast.success("Member removed");
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      role: member.role,
      color: member.color,
      avatar_url: member.avatar_url || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingMember(null);
    setFormData({
      name: "",
      role: "Other",
      color: "#6366f1",
      avatar_url: "",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="members-page">
      {/* Hidden file input for uploads */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={(e) => {
          const memberId = e.target.dataset.memberId;
          if (memberId) handleFileUpload(e, memberId);
        }}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Outfit']">
            Family Members
          </h1>
          <p className="text-slate-400 mt-1">
            {members.length} member{members.length !== 1 ? "s" : ""} in your
            family
          </p>
        </div>
        <Button
          onClick={() => setModalOpen(true)}
          className="bg-indigo-600 hover:bg-indigo-500 glow-primary"
          data-testid="add-member-button"
        >
          <Plus size={18} className="mr-2" />
          Add Member
        </Button>
      </div>

      {/* Members Grid */}
      {members.length === 0 ? (
        <div className="card-base p-12 text-center">
          <User className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">No family members yet</p>
          <p className="text-slate-500 text-sm mt-1">
            Add your first family member to get started
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((member, index) => (
            <div
              key={member.id}
              className={`card-base p-6 animate-fade-in stagger-${
                (index % 6) + 1
              }`}
              data-testid={`member-card-${member.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="relative group">
                  <div
                    className="w-16 h-16 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center"
                    style={{
                      borderWidth: 3,
                      borderStyle: "solid",
                      borderColor: member.color,
                    }}
                  >
                    {uploadingFor === member.id ? (
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    ) : member.avatar_url ? (
                      <img
                        src={member.avatar_url}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User size={28} className="text-slate-500" />
                    )}
                  </div>
                  {/* Upload overlay */}
                  <button
                    onClick={() => triggerFileUpload(member.id)}
                    className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    data-testid={`upload-avatar-${member.id}`}
                  >
                    <Camera size={20} className="text-white" />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditModal(member)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    data-testid={`edit-member-${member.id}`}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => deleteMember(member.id)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
                    data-testid={`delete-member-${member.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-white">{member.name}</h3>
              <div className="flex items-center gap-2 mt-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: member.color }}
                />
                <span className="text-slate-400">{member.role}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Member Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">
              {editingMember ? "Edit Member" : "Add Family Member"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Name</label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Enter name"
                className="bg-slate-950 border-slate-800"
                data-testid="member-name-input"
              />
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Role</label>
              <Select
                value={formData.role}
                onValueChange={(val) =>
                  setFormData({ ...formData, role: val })
                }
              >
                <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="member-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Color</label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, color: color.value })
                    }
                    className={`w-8 h-8 rounded-full transition-transform ${
                      formData.color === color.value
                        ? "ring-2 ring-white ring-offset-2 ring-offset-slate-900 scale-110"
                        : ""
                    }`}
                    style={{ backgroundColor: color.value }}
                    data-testid={`member-color-${color.label.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">
                Avatar (optional)
              </label>
              <div className="flex gap-2 mb-2">
                {defaultAvatars.map((url, index) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, avatar_url: url })
                    }
                    className={`w-12 h-12 rounded-full overflow-hidden border-2 transition-all ${
                      formData.avatar_url === url
                        ? "border-indigo-500 scale-110"
                        : "border-slate-700"
                    }`}
                    data-testid={`avatar-preset-${index}`}
                  >
                    <img
                      src={url}
                      alt={`Avatar ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
              <Input
                value={formData.avatar_url}
                onChange={(e) =>
                  setFormData({ ...formData, avatar_url: e.target.value })
                }
                placeholder="Or paste image URL"
                className="bg-slate-950 border-slate-800"
                data-testid="member-avatar-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="flex-1 border-slate-700"
                data-testid="member-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-indigo-600 hover:bg-indigo-500"
                data-testid="member-submit-button"
              >
                {editingMember ? "Update" : "Add"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
